import type { FC, ReactNode } from "react";
import { useState } from "react";
import { Translate, useTranslation } from "@privacy/translations";
import { useMutation } from "@tanstack/react-query";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { SystemIcon2, PencilIcon, ArchiveIcon, RotateCcwIcon } from "../../../shared/icons";
import {
  prefetchSystemById,
  systemHistoryQueryOptions,
  unarchiveSystemMutationOptions,
  useDeleteSystem,
} from "../../../api/systems";
import { prefetch } from "../../../api/prefetch";
import { usePermission } from "../../../shared/permissions";
import type { TranslationKey } from "@privacy/translations";
import type { SystemById } from "../../../api/systems.types";
import type { DrawerHeaderProps } from "../../../shared/Drawer";
import { Drawer } from "../../../shared/Drawer";
import { trackEvent } from "../../../shared/Snowplow";
import { usePrivacyNavigate } from "../../../shared/usePrivacyNavigate";
import { SystemEditDialog } from "./SystemEditDialog";
import { SystemReplaceAndArchiveDialog } from "./SystemReplaceAndArchiveDialog";
import { ConfirmDeleteDialog } from "../../../shared/ConfirmDeleteDialog";
import { ActionMenu, ActionMenuDeleteMenuItem, ActionMenuItemWithPermission } from "../../../shared/ActionMenu";

type SystemsDrawerHeaderProps = {
  system: SystemById;
  statusIndicator: DrawerHeaderProps["status"];
  subDrawerHeader?: ReactNode;
  activeTab: "overview" | "history";
};

type DialogState = "edit" | "archive" | "unarchiveConfirm" | null;
export const SystemsDrawerHeader: FC<SystemsDrawerHeaderProps> = ({
  system,
  statusIndicator,
  activeTab,
  subDrawerHeader,
}) => {
  const { t } = useTranslation();
  const { paths, navigate } = usePrivacyNavigate();

  return (
    <>
      <Drawer.Header
        status={statusIndicator}
        archivedAt={system.archivedAt}
        onClose={() => navigate(paths.systemsGrid())}
        title={system.name}
        renderIcon={props => <SystemIcon2 {...props} />}
        topRightSlot={
          <SystemActionMenu
            systemId={system.id}
            systemName={system.name}
            archivedAt={system.archivedAt}
            onDeleted={() => navigate(paths.systemsGrid())}
            ariaLabelKey="GridDrawer.Menu.ButtonLabel"
          />
        }
        bottomSlot={
          <Stack sx={{ px: Drawer.PADDING_X }}>
            <Drawer.TabButtons>
              <Drawer.TabButton
                active={activeTab === "overview"}
                to={paths.systemsInline(system.id)}
                onClick={() => trackEvent.clickSystemHistoryTab()}
              >
                {t("Dynamic.Drawer.OverviewButton")}
              </Drawer.TabButton>
              <Drawer.TabButton
                active={activeTab === "history"}
                to={paths.systemsHistory(system.id)}
                onClick={() => trackEvent.clickSystemHistoryTab()}
                onHover={() => prefetch(systemHistoryQueryOptions(system.id))}
              >
                {t("Dynamic.Drawer.HistoryButton")}
              </Drawer.TabButton>
            </Drawer.TabButtons>
          </Stack>
        }
      />
      {subDrawerHeader}
    </>
  );
};

type SystemActionMenuProps = {
  systemId: string;
  systemName: string;
  archivedAt?: string;
  onDeleted: () => void;
  ariaLabelKey?: TranslationKey;
};
export const SystemActionMenu = ({
  systemId,
  systemName,
  archivedAt,
  onDeleted,
  ariaLabelKey,
}: SystemActionMenuProps) => {
  const { hasPermission } = usePermission();
  const { t } = useTranslation();
  const { mutateAsync: deleteSystem } = useDeleteSystem();
  const unarchiveMutation = useMutation(unarchiveSystemMutationOptions(systemId));
  const [isHeaderMenuOpen, setIsHeaderMenuOpen] = useState(false);
  const [dialogState, setDialogState] = useState<DialogState>(null);
  const canUpdate = hasPermission("nova-forms-api:system:update");
  const isArchived = Boolean(archivedAt);

  const closeDialog = () => {
    setDialogState(null);
    setIsHeaderMenuOpen(false);
  };

  const openDialog = (next: DialogState) => {
    setDialogState(next);
    setIsHeaderMenuOpen(false);
  };

  return (
    <>
      {dialogState === "edit" && <SystemEditDialog onClose={closeDialog} systemId={systemId} systemName={systemName} />}
      {dialogState === "archive" && (
        <SystemReplaceAndArchiveDialog onClose={closeDialog} systemId={systemId} systemName={systemName} />
      )}
      {dialogState === "unarchiveConfirm" && (
        <ConfirmDeleteDialog
          isOpen
          onClose={closeDialog}
          onDelete={() => unarchiveMutation.mutateAsync()}
          headerText={t("System.Archive.Unarchive.Title")}
          message={
            <Translate
              translationKey="System.Archive.Unarchive.Message"
              values={{
                name: (
                  <Box component="span" sx={{ fontWeight: "bold" }}>
                    {systemName}
                  </Box>
                ),
              }}
            />
          }
          confirmButtonLabel={t("System.Archive.Unarchive.Button")}
          id="confirm-unarchive-system"
          onKeyDown={e => e.key === "Tab" && e.stopPropagation()}
        />
      )}
      <ActionMenu isOpen={isHeaderMenuOpen} onOpenChange={setIsHeaderMenuOpen} ariaLabelKey={ariaLabelKey}>
        {!isArchived && (
          <ActionMenuItemWithPermission missingPermission={!canUpdate} onClick={() => openDialog("edit")}>
            <PencilIcon />
            {t("SystemsDrawer.Edit.Label")}
          </ActionMenuItemWithPermission>
        )}

        {isArchived ? (
          <ActionMenuItemWithPermission missingPermission={!canUpdate} onClick={() => openDialog("unarchiveConfirm")}>
            <RotateCcwIcon />
            {t("System.Archive.RestoreLabel")}
          </ActionMenuItemWithPermission>
        ) : (
          <ActionMenuItemWithPermission
            missingPermission={!canUpdate}
            onMouseOver={() => prefetchSystemById(systemId)}
            onClick={() => openDialog("archive")}
          >
            <ArchiveIcon />
            {t("System.Archive.Label")}
          </ActionMenuItemWithPermission>
        )}

        <ActionMenuDeleteMenuItem
          missingPermission={!hasPermission("nova-forms-api:system:delete")}
          confirmDeleteDialog={{
            header: t("SystemsDrawer.Delete.ConfirmHeader"),
            message: (
              <>
                <Translate
                  translationKey="SystemsDrawer.Delete.ConfirmMessage"
                  values={{
                    name: (
                      <Box component="span" sx={{ fontWeight: "bold" }}>
                        {systemName}
                      </Box>
                    ),
                  }}
                />
                {!isArchived && ` ${t("SystemsDrawer.Delete.RecommendArchive")}`}
              </>
            ),
            delete: t("SystemsDrawer.Delete.ConfirmDelete"),
          }}
          onClose={() => setIsHeaderMenuOpen(false)}
          onDelete={() => deleteSystem(systemId, { onSuccess: () => onDeleted() })}
        />
      </ActionMenu>
    </>
  );
};
